import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { 
  Briefcase,
  CheckCircle2,
  Loader2,
  ArrowRight,
  XCircle,
  Sparkles
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { trackPurchase } from "../utils/analytics";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, updateUser } = useAuth();
  const [status, setStatus] = useState("loading");
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [error, setError] = useState(null);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setStatus("error");
      setError("Session de paiement invalide");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_URL}/api/subscription/checkout/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = response.data;
      setSubscriptionData(data);

      if (data.payment_status === "paid" || data.status === "complete") {
        setStatus("success");
        // Update user subscription tier in context
        if (data.plan_id) {
          updateUser({ subscription_tier: data.plan_id });
        }
        // GA4 purchase event (de-duplicated by transaction_id on GA4 side)
        const planPriceMap = {
          veille_mensuel: 90,
          veille_trimestriel: 270,
          veille_semestriel: 432,
          veille_annuel: 756,
          pack_memoires: 149,
        };
        const value =
          (typeof data.amount === "number" && data.amount) ||
          planPriceMap[data.plan_id] ||
          0;
        trackPurchase({
          transactionId: sessionId,
          planId: data.plan_id || "subscription",
          value,
        });
      } else if (data.payment_status === "failed") {
        setStatus("failed");
        setError("Le paiement a échoué");
      } else {
        // Payment still processing, poll again
        setTimeout(checkPaymentStatus, 2000);
      }
    } catch (err) {
      console.error("Error checking payment status:", err);
      setStatus("error");
      setError(err.response?.data?.detail || "Erreur lors de la vérification du paiement");
    }
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-900 via-indigo-900 to-slate-900 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <Loader2 className="w-16 h-16 text-white animate-spin mx-auto mb-4" />
          <p className="text-white text-xl">Vérification du paiement...</p>
        </motion.div>
      </div>
    );
  }

  if (status === "error" || status === "failed") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 via-rose-900 to-slate-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="max-w-md w-full">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">
                Paiement non complété
              </h1>
              <p className="text-slate-600 mb-6">
                {error || "Une erreur s'est produite lors du paiement."}
              </p>
              <div className="space-y-3">
                <Button
                  className="w-full bg-violet-700 hover:bg-violet-800"
                  onClick={() => navigate("/pricing")}
                  data-testid="retry-subscription"
                >
                  Réessayer
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate("/dashboard")}
                  data-testid="back-to-dashboard-error"
                >
                  Retour au dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-900 via-indigo-900 to-slate-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg"
      >
        {/* Success Animation */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", delay: 0.2 }}
          className="text-center mb-8"
        >
          <div className="w-24 h-24 rounded-full bg-green-500 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-500/30">
            <CheckCircle2 className="w-12 h-12 text-white" />
          </div>
        </motion.div>

        <Card className="bg-white/95 backdrop-blur-sm shadow-2xl">
          <CardContent className="p-8 text-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <h1 className="text-3xl font-bold text-slate-900 mb-2">
                Abonnement activé !
              </h1>
              <p className="text-slate-600 mb-6">
                Merci pour votre confiance. Votre abonnement est maintenant actif.
              </p>

              {subscriptionData && (
                <div className="bg-slate-50 rounded-xl p-6 mb-6 text-left">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-violet-600" />
                    Détails de votre abonnement
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Plan</span>
                      <span className="font-medium text-slate-900">{subscriptionData.plan_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Montant</span>
                      <span className="font-medium text-slate-900">{subscriptionData.amount}€</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Statut</span>
                      <span className="font-medium text-green-600">Actif</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <Button
                  className="w-full bg-violet-700 hover:bg-violet-800 py-6 text-lg"
                  onClick={() => navigate("/dashboard")}
                  data-testid="go-to-dashboard"
                >
                  Accéder à mes marchés
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate("/profile")}
                  data-testid="complete-profile"
                >
                  Compléter mon profil entreprise
                </Button>
              </div>
            </motion.div>
          </CardContent>
        </Card>

        {/* Logo */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-center mt-8"
        >
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors">
            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
              <Briefcase className="w-4 h-4 text-white" />
            </div>
            <span className="font-medium">Mon DCE Express</span>
          </Link>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default SubscriptionSuccessPage;
